import { useCallback, useEffect, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  LogIn,
  LogOut,
  MessageSquarePlus,
} from "lucide-react";
import PageContainer from "../../components/PageContainer";
import {
  Button,
  FormField,
  LoadingState,
  IconButton,
  PageHeader,
  SectionCard,
  StatusBadge,
  TextInput,
} from "../../components/ui";
import SolicitarAjusteModal from "./SolicitarAjusteModal";
import { Tabela, Td, Th } from "../GestaoPonto/TabelaCompacta";
import { pontoService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import {
  competenciaAtual,
  dataCurta,
  deslocarCompetencia,
  nomeCompetencia,
  quatroColunas,
} from "../GestaoPonto/pontoUtils";

const ROTULOS_MARCACAO = ["Entrada", "Saída", "Entrada", "Saída"];

const SITUACAO_SOLICITACAO = {
  PENDENTE: { rotulo: "Aguardando gestão", variante: "warning" },
  APROVADO: { rotulo: "Aprovado", variante: "success" },
  NEGADO: { rotulo: "Negado", variante: "danger" },
};

// "segunda-feira, 02 de novembro" -> "Segunda-feira, 02 de novembro"
const dataPorExtenso = (data) => {
  const texto = data.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

// Hora atual "HH:mm", usada para pré-preencher o horário da batida.
const horaAtual = () => {
  const agora = new Date();
  return `${String(agora.getHours()).padStart(2, "0")}:${String(agora.getMinutes()).padStart(2, "0")}`;
};

const RelogioAoVivo = () => {
  const [agora, setAgora] = useState(new Date());

  useEffect(() => {
    const intervalo = setInterval(() => setAgora(new Date()), 1000);
    return () => clearInterval(intervalo);
  }, []);

  return (
    <div className="text-center">
      <p className="text-5xl font-bold tabular-nums tracking-tight text-gray-900 sm:text-6xl">
        {agora.toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })}
      </p>
      <p className="mt-1 text-sm text-gray-500">{dataPorExtenso(agora)}</p>
    </div>
  );
};

/**
 * Tela da colaboradora: registra entrada e saída e mostra só os próprios horários.
 * Não mostra horas trabalhadas, atrasos, faltas nem horas extras.
 */
const MeuPonto = () => {
  const { showError, showSuccess } = useNotification();
  const [competencia, setCompetencia] = useState(competenciaAtual());
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [registrando, setRegistrando] = useState(false);
  const travaRegistro = useRef(false);
  const [horario, setHorario] = useState(horaAtual);
  const [diaAjuste, setDiaAjuste] = useState(null);
  const [enviandoAjuste, setEnviandoAjuste] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setDados(await pontoService.meuPonto(competencia));
    } catch (erro) {
      showError(erro.message);
    } finally {
      setCarregando(false);
    }
  }, [competencia]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    carregar();
  }, [carregar]);

  const registrar = async () => {
    if (travaRegistro.current) return;
    travaRegistro.current = true;
    setRegistrando(true);
    try {
      const registro = await pontoService.registrar(horario);
      const tipo = registro.tipo === "ENTRADA" ? "Entrada" : "Saída";
      showSuccess(`${tipo} registrada às ${registro.horario}.`);
      setHorario(horaAtual());
      await carregar();
    } catch (erro) {
      showError(erro.message);
    } finally {
      travaRegistro.current = false;
      setRegistrando(false);
    }
  };

  const enviarAjuste = async (pedido) => {
    setEnviandoAjuste(true);
    try {
      await pontoService.solicitarAjuste(pedido);
      showSuccess("Solicitação enviada. A gestão vai analisar.");
      setDiaAjuste(null);
      await carregar();
    } catch (erro) {
      showError(erro.message);
    } finally {
      setEnviandoAjuste(false);
    }
  };

  if (carregando && !dados) {
    return (
      <PageContainer className="p-6">
        <LoadingState label="Carregando o ponto..." />
      </PageContainer>
    );
  }

  const proxima = dados?.proximaMarcacao;
  const podeRegistrar = dados?.participaDoPonto && proxima;
  const marcacoesHoje = quatroColunas(dados?.marcacoesHoje);
  const pendentes = new Set(
    (dados?.solicitacoes || [])
      .filter((solicitacao) => solicitacao.status === "PENDENTE")
      .map((solicitacao) => solicitacao.data),
  );

  return (
    <PageContainer className="p-4 sm:p-6">
      <PageHeader
        title="Bater ponto"
        description={dados?.nome ? `Olá, ${dados.nome.split(" ")[0]}!` : null}
      />

      <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
        <div className="space-y-6 self-start">
          <SectionCard bodyClassName="p-6 space-y-6">
            <RelogioAoVivo />

            {!dados?.participaDoPonto && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                Você ainda não está cadastrada no controle de ponto. Procure a
                gestão.
              </div>
            )}

            {proxima && (
              <FormField
                label={
                  proxima === "SAIDA"
                    ? "Horário em que você saiu"
                    : "Horário em que você chegou"
                }
                htmlFor="ponto-horario-manual"
                hint="Confira e ajuste antes de registrar, se precisar."
              >
                <TextInput
                  id="ponto-horario-manual"
                  type="time"
                  className="text-center text-2xl font-semibold tabular-nums"
                  value={horario}
                  disabled={!podeRegistrar}
                  onChange={(evento) => setHorario(evento.target.value)}
                />
              </FormField>
            )}

            <Button
              variant={proxima === "SAIDA" ? "warning" : "success"}
              icon={proxima === "SAIDA" ? LogOut : LogIn}
              className="min-h-16 w-full text-lg"
              disabled={!podeRegistrar || !horario}
              loading={registrando}
              onClick={registrar}
            >
              {registrando
                ? "Registrando..."
                : proxima === "SAIDA"
                  ? `Registrar saída às ${horario || "--:--"}`
                  : proxima === "ENTRADA"
                    ? `Registrar entrada às ${horario || "--:--"}`
                    : dados?.participaDoPonto
                      ? "Marcações de hoje concluídas"
                      : "Ponto não disponível"}
            </Button>

            <div>
              <p className="mb-2 text-sm font-medium text-gray-700">
                Marcações de hoje
              </p>
              <div className="grid grid-cols-4 gap-2">
                {marcacoesHoje.map((horario, indice) => (
                  <div
                    key={indice}
                    className={`rounded-lg border px-2 py-3 text-center ${
                      horario
                        ? "border-blue-200 bg-blue-50"
                        : "border-dashed border-gray-300 bg-white"
                    }`}
                  >
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">
                      {ROTULOS_MARCACAO[indice]}
                    </p>
                    <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">
                      {horario || "--:--"}
                    </p>
                  </div>
                ))}
              </div>
              <p className="mt-3 flex items-center gap-1 text-xs text-gray-500">
                <Clock size={12} />
                Se esqueceu de registrar em outro dia, use “Solicitar ajuste” na
                tabela.
              </p>
            </div>
          </SectionCard>

          <SectionCard bodyClassName="p-0">
            <div className="border-b border-gray-200 px-5 py-4">
              <h2 className="font-semibold text-gray-900">
                Minhas solicitações
              </h2>
              <p className="text-xs text-gray-500">
                Errou ou esqueceu de bater? Use o botão de ajuste no dia, na
                tabela ao lado.
              </p>
            </div>
            {(dados?.solicitacoes || []).length === 0 ? (
              <p className="px-5 py-4 text-sm text-gray-500">
                Nenhuma solicitação em {nomeCompetencia(competencia)}.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {dados.solicitacoes.map((solicitacao) => {
                  const situacao = SITUACAO_SOLICITACAO[solicitacao.status];
                  return (
                    <li
                      key={solicitacao.id}
                      className="space-y-1 px-5 py-3 text-sm"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-gray-900">
                          {dataCurta(solicitacao.data)} ·{" "}
                          {solicitacao.descricao}
                        </span>
                        <StatusBadge variant={situacao?.variante}>
                          {situacao?.rotulo || solicitacao.status}
                        </StatusBadge>
                      </div>
                      <p className="text-gray-600">{solicitacao.motivo}</p>
                      {solicitacao.resposta && (
                        <p className="text-gray-500">
                          Resposta: {solicitacao.resposta}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>
        </div>

        <SectionCard bodyClassName="p-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
            <h2 className="font-semibold text-gray-900">Meus horários</h2>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                icon={ChevronLeft}
                aria-label="Mês anterior"
                onClick={() =>
                  setCompetencia((atual) => deslocarCompetencia(atual, -1))
                }
              />
              <span className="min-w-[140px] text-center text-sm font-medium text-gray-800">
                {nomeCompetencia(competencia)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                icon={ChevronRight}
                aria-label="Próximo mês"
                disabled={competencia >= competenciaAtual()}
                onClick={() =>
                  setCompetencia((atual) => deslocarCompetencia(atual, 1))
                }
              />
            </div>
          </div>

          <Tabela>
            <thead>
              <tr>
                <Th>Data</Th>
                <Th>Dia</Th>
                {ROTULOS_MARCACAO.map((rotulo, indice) => (
                  <Th key={indice} className="text-center">
                    {rotulo}
                  </Th>
                ))}
                <Th>Observação</Th>
                <Th className="text-right">Ajuste</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {(dados?.dias || []).map((dia) => (
                <tr
                  key={dia.data}
                  className={
                    dia.data === dados?.hoje
                      ? "bg-blue-50"
                      : dia.diaSemana === "Domingo" || dia.ocorrencia
                        ? "bg-gray-50"
                        : ""
                  }
                >
                  <Td className="tabular-nums">{dataCurta(dia.data)}</Td>
                  <Td>{dia.diaSemana}</Td>
                  {quatroColunas(dia.marcacoes).map((horario, indice) => (
                    <Td key={indice} className="text-center tabular-nums">
                      {horario}
                    </Td>
                  ))}
                  <Td className="text-gray-500">{dia.ocorrencia || ""}</Td>
                  <Td className="text-right">
                    {pendentes.has(dia.data) && (
                      <StatusBadge variant="warning" className="mr-2">
                        pendente
                      </StatusBadge>
                    )}
                    {dados?.participaDoPonto && dia.data <= dados.hoje && (
                      <IconButton
                        size="sm"
                        variant="info"
                        icon={MessageSquarePlus}
                        label={`Solicitar ajuste em ${dataCurta(dia.data)}`}
                        onClick={() => setDiaAjuste(dia)}
                      />
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        </SectionCard>
      </div>
      <SolicitarAjusteModal
        dia={diaAjuste}
        enviando={enviandoAjuste}
        onCancelar={() => setDiaAjuste(null)}
        onEnviar={enviarAjuste}
      />
    </PageContainer>
  );
};

export default MeuPonto;
